import { AddCircleOutlined, BoltOutlined, DeleteOutlined } from "@mui/icons-material";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type Dispatch, type FC, type SetStateAction, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { EditTimePopover } from "../../../../components/EditTimePopover/EditTimePopover";
import { DaysInWeek, DaysInWeekSelector } from "../../../../components/RecurringShift/util/DaysInWeekSelector";
import { type SystemChipItem, SystemChips } from "../../../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { EmptyState } from "../../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../../components/UIToolkit/molecules/SettingsSection";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { ConfigMultiSelectCell } from "../../../../components/UIToolkit/Tables/ConfigTable/ConfigMultiSelectCell";
import {
	BORDERLESS_INPUT_SX,
	CONFIG_TABLE_BODY_CELL_SX,
	CONFIG_TABLE_BODY_ROW_SX,
	CONFIG_TABLE_CONTAINER_SX,
	CONFIG_TABLE_HEADER_CELL_SX,
} from "../../../../components/UIToolkit/Tables/ConfigTable/configTableSx";
import { useAllJobs } from "../../../../hooks/useJobs";
import { NEUTRAL } from "../../../../theme/tokens";
import { parseTimeString } from "../../../../utils/dateUtils";
import { normalizeSelectedJobIds, selectedJobIdsForDisplay } from "../../../../utils/jobSelection";
import type { AutoApproveRule, ShiftHourWindow } from "../../../../utils/models";
import type { GeneralFormData } from "./types";

// Short weekday codes stored on the rule (Mon..Sun). Ordered Monday-first to match the
// mockup's M T W T F S S day toggles. `shiftDays: []` means "all days".
const WEEKDAY_CODES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
type WeekdayCode = (typeof WEEKDAY_CODES)[number];
const WEEKDAY_PRESETS = {
	weekday: ["Mon", "Tue", "Wed", "Thu", "Fri"] as WeekdayCode[],
	weekend: ["Sat", "Sun"] as WeekdayCode[],
};
// "All Days" is not a WEEKDAY_PRESETS entry: it stores `shiftDays: []`, the same value the backend
// evaluator reads as "every day", so there is a single representation of an all-week rule.
const ALL_DAYS_PRESET = "allDays";
type DayPreset = typeof ALL_DAYS_PRESET | keyof typeof WEEKDAY_PRESETS;

// Bridge our stored "Mon".."Sun" codes ↔ DaysInWeekSelector's `DaysInWeek` enum ("Monday"..).
const CODE_TO_DAY: Record<WeekdayCode, DaysInWeek> = {
	Mon: DaysInWeek.monday,
	Tue: DaysInWeek.tuesday,
	Wed: DaysInWeek.wednesday,
	Thu: DaysInWeek.thursday,
	Fri: DaysInWeek.friday,
	Sat: DaysInWeek.saturday,
	Sun: DaysInWeek.sunday,
};
// DaysInWeekSelector renders Sunday-first at weekStartDay=0; we want Monday-first.
const MONDAY_WEEK_START = 1;
const DAYS_IN_WEEK = 7;
const DEFAULT_WINDOW: ShiftHourWindow = { start: "07:00", end: "19:00" };

const ALL_WEEK_DAYS: DaysInWeek[] = WEEKDAY_CODES.map((code) => CODE_TO_DAY[code]);

const toShiftDaysCode = (days: DaysInWeek[]): WeekdayCode[] => WEEKDAY_CODES.filter((code) => days.includes(CODE_TO_DAY[code]));

export const AutoApproveRulesTable: FC<{
	formData: GeneralFormData;
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
}> = ({ formData, setFormData }) => {
	const { t } = useTranslation();
	// Which window's time popover is open, keyed as `${rowIndex}-${windowIndex}`, plus its anchor.
	const [openWindow, setOpenWindow] = useState<{ key: string; anchorEl: HTMLElement }>();
	const rules = formData.autoApproveRules ?? [];
	const { data: allJobs } = useAllJobs();
	const departmentJobs = useMemo(() => {
		const selected = new Set(formData.jobIds ?? []);
		return (allJobs ?? []).filter((j) => selected.has(j.id));
	}, [allJobs, formData.jobIds]);
	const showJobColumn = departmentJobs.length > 0;

	const addRow = useCallback(() => {
		setFormData((prev) => ({
			...prev,
			autoApproveRules: [
				...(prev.autoApproveRules ?? []),
				{
					id: uuidv4(),
					name: "",
					jobIds: [],
					maxOt: null,
					timeBeforeShift: null,
					staffingShortage: null,
					shiftDays: [],
					shiftHours: [],
					departmentId: prev.id ?? "",
				} satisfies AutoApproveRule,
			],
		}));
	}, [setFormData]);

	const removeRow = useCallback(
		(index: number) => {
			setFormData((prev) => ({
				...prev,
				autoApproveRules: (prev.autoApproveRules ?? []).filter((_, i) => i !== index),
			}));
		},
		[setFormData],
	);

	const updateRow = useCallback(
		(index: number, patch: Partial<AutoApproveRule>) => {
			setFormData((prev) => {
				const list = [...(prev.autoApproveRules ?? [])];
				list[index] = { ...list[index], ...patch };
				return { ...prev, autoApproveRules: list };
			});
		},
		[setFormData],
	);

	// Human-readable summary of a rule (read-only), translated. Mirrors the mockup's getRuleLogic:
	// only the enabled gates/selectors are listed, joined by AND; an empty rule reads as "always".
	const ruleLogic = useCallback(
		(rule: AutoApproveRule): string => {
			const parts: string[] = [];
			if (rule.maxOt != null) parts.push(t("department.config.staffApp.autoApprove.logic.maxOt", { hours: rule.maxOt }));
			if (rule.timeBeforeShift != null) parts.push(t("department.config.staffApp.autoApprove.logic.timeBeforeShift", { hours: rule.timeBeforeShift }));
			if (rule.staffingShortage != null) parts.push(t("department.config.staffApp.autoApprove.logic.staffingShortage", { count: rule.staffingShortage }));
			if ((rule.shiftDays ?? []).length > 0) parts.push(t("department.config.staffApp.autoApprove.logic.days", { count: rule.shiftDays.length }));
			if ((rule.shiftHours ?? []).length > 0) parts.push(t("department.config.staffApp.autoApprove.logic.windows", { count: rule.shiftHours.length }));
			if (parts.length === 0) return t("department.config.staffApp.autoApprove.logic.always");
			return t("department.config.staffApp.autoApprove.logic.prefix", { conditions: parts.join(t("department.config.staffApp.autoApprove.logic.and")) });
		},
		[t],
	);

	// One shared item so `selectedItems` can point at the exact chip "All Days" renders.
	const allDaysChip = useMemo<SystemChipItem<DayPreset>>(
		() => ({ value: ALL_DAYS_PRESET, label: t("department.config.staffApp.autoApprove.days.allDays") }),
		[t],
	);
	const dayPresetChips = useMemo<SystemChipItem<DayPreset>[]>(
		() => [
			allDaysChip,
			{ value: "weekday", label: t("department.config.staffApp.autoApprove.days.weekday") },
			{ value: "weekend", label: t("department.config.staffApp.autoApprove.days.weekend") },
		],
		[allDaysChip, t],
	);

	const jobChipItems = useMemo(
		() => departmentJobs.map((j) => ({ value: j.id, label: j.displayName ?? j.name, selectedLabel: j.displayName ?? j.name })),
		[departmentJobs],
	);

	// Convert a wall-clock "HH:MM" window string into a Luxon DateTime (today) for EditTimePopover.
	const windowTimeToDateTime = (time: string): DateTime => {
		const { hour, minute } = parseTimeString(time);
		return DateTime.now().set({ hour, minute, second: 0, millisecond: 0 });
	};

	// A single start–end window row within the Shift Hours cell.
	const renderWindow = (rowIndex: number, windows: ShiftHourWindow[], windowIndex: number, window: ShiftHourWindow) => {
		const key = `${rowIndex}-${windowIndex}`;
		const patchWindow = (patch: Partial<ShiftHourWindow>) => {
			const next = windows.map((w, i) => (i === windowIndex ? { ...w, ...patch } : w));
			updateRow(rowIndex, { shiftHours: next });
		};
		const removeWindow = () => updateRow(rowIndex, { shiftHours: windows.filter((_, i) => i !== windowIndex) });
		const closePopover = () => setOpenWindow(undefined);
		const isOpen = openWindow?.key === key;
		return (
			<Box key={windowIndex} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				<SystemButton
					buttonVariant="borderless"
					size="small"
					onClick={(event) => setOpenWindow({ key, anchorEl: event.currentTarget })}
					aria-label={`${t("department.config.staffApp.autoApprove.col.shiftHours.start")} – ${t("department.config.staffApp.autoApprove.col.shiftHours.end")}`}
					data-testid={`dept-config-auto-approve-window-start-${rowIndex}-${windowIndex}`}
				>
					{`${window.start} – ${window.end}`}
				</SystemButton>
				{isOpen && openWindow && (
					<EditTimePopover
						startTime={windowTimeToDateTime(window.start)}
						endTime={windowTimeToDateTime(window.end)}
						anchorEl={openWindow.anchorEl}
						onTimeChange={({ newStartTime, newEndTime }: { newStartTime: DateTime; newEndTime: DateTime }) => {
							patchWindow({ start: newStartTime.toFormat("HH:mm"), end: newEndTime.toFormat("HH:mm") });
							closePopover();
						}}
						onClose={closePopover}
						hidePatialShiftLengths
					/>
				)}
				<SystemButton
					buttonVariant="borderless"
					size="small"
					leadingIcon={<DeleteOutlined fontSize="small" />}
					onClick={removeWindow}
					tooltip={t("department.config.staffApp.autoApprove.removeWindow")}
					aria-label={t("department.config.staffApp.autoApprove.removeWindow")}
					data-testid={`dept-config-auto-approve-window-remove-${rowIndex}-${windowIndex}`}
				/>
			</Box>
		);
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
			<SettingsSection
				headerOnly
				title={t("department.config.staffApp.autoApprove.rules.section")}
				description={t("department.config.staffApp.autoApprove.rules.description")}
				dataTestId="dept-config-auto-approve-rules-header"
				action={
					<SystemButton
						buttonVariant="primary"
						size="small"
						leadingIcon={<AddCircleOutlined />}
						onClick={addRow}
						data-testid="dept-config-auto-approve-add-row"
					>
						{t("department.config.staffApp.autoApprove.addSet")}
					</SystemButton>
				}
			/>
			<TableContainer component={Paper} sx={CONFIG_TABLE_CONTAINER_SX}>
				<Table sx={{ width: "100%" }}>
					{/* Header (and its wide per-column min-widths) only when there are rows — otherwise the
					    empty state would render under stretched columns and force a horizontal scrollbar. */}
					{rules.length > 0 && (
						<TableHead>
							<TableRow>
								<TableCell sx={[CONFIG_TABLE_HEADER_CELL_SX, { minWidth: "90px" }]}>{t("department.config.staffApp.autoApprove.col.ruleSet")}</TableCell>
								<TableCell sx={[CONFIG_TABLE_HEADER_CELL_SX, { minWidth: "90px" }]}>{t("department.config.staffApp.autoApprove.col.maxOt")}</TableCell>
								{showJobColumn && (
									<TableCell sx={[CONFIG_TABLE_HEADER_CELL_SX, { minWidth: "120px" }]}>{t("department.config.staffApp.autoApprove.col.jobTypes")}</TableCell>
								)}
								<TableCell sx={[CONFIG_TABLE_HEADER_CELL_SX, { minWidth: "120px" }]}>
									{t("department.config.staffApp.autoApprove.col.timeBeforeShift")}
								</TableCell>
								<TableCell sx={[CONFIG_TABLE_HEADER_CELL_SX, { minWidth: "120px" }]}>
									{t("department.config.staffApp.autoApprove.col.staffingShortage")}
								</TableCell>
								<TableCell sx={[CONFIG_TABLE_HEADER_CELL_SX, { minWidth: "200px" }]}>{t("department.config.staffApp.autoApprove.col.shiftDays")}</TableCell>
								<TableCell sx={[CONFIG_TABLE_HEADER_CELL_SX, { minWidth: "200px" }]}>{t("department.config.staffApp.autoApprove.col.shiftHours")}</TableCell>
								<TableCell sx={[CONFIG_TABLE_HEADER_CELL_SX, { minWidth: "200px" }]}>{t("department.config.staffApp.autoApprove.col.ruleLogic")}</TableCell>
								<TableCell sx={[CONFIG_TABLE_HEADER_CELL_SX, { minWidth: "60px" }]}>{t("department.config.staffApp.autoApprove.col.actions")}</TableCell>
							</TableRow>
						</TableHead>
					)}
					<TableBody>
						{rules.map((row, index) => {
							const windows = row.shiftHours ?? [];
							// `shiftDays: []` is how "all days" is stored, so it must light every circle — not none.
							const isAllDays = row.shiftDays.length === 0;
							const selectedDays = isAllDays ? ALL_WEEK_DAYS : row.shiftDays.map((code) => CODE_TO_DAY[code as WeekdayCode]).filter(Boolean);
							return (
								<TableRow key={row.id ?? index} sx={CONFIG_TABLE_BODY_ROW_SX}>
									<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
										<TextField
											size="small"
											fullWidth
											sx={BORDERLESS_INPUT_SX}
											value={row.name}
											onChange={(e) => updateRow(index, { name: e.target.value })}
											placeholder={t("department.config.staffApp.autoApprove.col.ruleSet")}
											autoComplete="off"
											// Off so the browser doesn't offer unrelated form-history values as suggestions.
											slotProps={{ htmlInput: { autoComplete: "off", "data-testid": `dept-config-auto-approve-name-${index}` } }}
										/>
									</TableCell>
									<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
										<TextField
											type="number"
											size="small"
											fullWidth
											sx={BORDERLESS_INPUT_SX}
											value={row.maxOt ?? ""}
											placeholder={t("department.config.staffApp.autoApprove.maxOt.none")}
											onChange={(e) => updateRow(index, { maxOt: e.target.value === "" ? null : Number(e.target.value) })}
											slotProps={{ htmlInput: { min: 0, step: 0.5, "data-testid": `dept-config-auto-approve-max-ot-${index}` } }}
										/>
									</TableCell>
									{showJobColumn && (
										<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
											<ConfigMultiSelectCell<string>
												aria-label={t("department.config.staffApp.autoApprove.col.jobTypes")}
												items={jobChipItems}
												selectedItems={selectedJobIdsForDisplay(row.jobIds, departmentJobs)}
												onChangeMultiple={(selectedValues) => {
													updateRow(index, { jobIds: normalizeSelectedJobIds(selectedValues as string[], departmentJobs) ?? [] });
												}}
												dataTestId={`dept-config-auto-approve-jobs-${index}`}
											/>
										</TableCell>
									)}
									<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
										<TextField
											type="number"
											size="small"
											fullWidth
											sx={BORDERLESS_INPUT_SX}
											value={row.timeBeforeShift ?? ""}
											placeholder={t("department.config.staffApp.autoApprove.timeBeforeShift.any")}
											onChange={(e) => updateRow(index, { timeBeforeShift: e.target.value === "" ? null : Number(e.target.value) })}
											slotProps={{ htmlInput: { min: 0, step: 1, "data-testid": `dept-config-auto-approve-time-before-${index}` } }}
										/>
									</TableCell>
									<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
										<TextField
											type="number"
											size="small"
											fullWidth
											sx={BORDERLESS_INPUT_SX}
											value={row.staffingShortage ?? ""}
											placeholder={t("department.config.staffApp.autoApprove.staffingShortage.any")}
											onChange={(e) => updateRow(index, { staffingShortage: e.target.value === "" ? null : Number(e.target.value) })}
											slotProps={{ htmlInput: { min: 0, step: 1, "data-testid": `dept-config-auto-approve-staffing-shortage-${index}` } }}
										/>
									</TableCell>
									<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
											<DaysInWeekSelector
												size="medium"
												weekStartDay={MONDAY_WEEK_START}
												selectedDays={selectedDays}
												onChange={(days) => updateRow(index, { shiftDays: days.length === DAYS_IN_WEEK ? [] : toShiftDaysCode(days) })}
											/>
											<SystemChips<DayPreset>
												variant="outlined"
												forceFitContent
												items={dayPresetChips}
												selectedItems={isAllDays ? [allDaysChip] : []}
												onChange={(item) => {
													if (item.value === ALL_DAYS_PRESET) updateRow(index, { shiftDays: [] });
													else updateRow(index, { shiftDays: WEEKDAY_PRESETS[item.value] });
												}}
												dataTestId={`dept-config-auto-approve-day-presets-${index}`}
											/>
										</Box>
									</TableCell>
									<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
											{windows.length === 0 && (
												<Typography variant="body2" sx={{ color: "text.secondary", pl: 0.5 }}>
													{t("department.config.staffApp.autoApprove.shiftHours.all")}
												</Typography>
											)}
											{windows.map((window, windowIndex) => renderWindow(index, windows, windowIndex, window))}
											<SystemButton
												buttonVariant="borderless"
												size="small"
												leadingIcon={<AddCircleOutlined fontSize="small" />}
												onClick={() => updateRow(index, { shiftHours: [...windows, { ...DEFAULT_WINDOW }] })}
												data-testid={`dept-config-auto-approve-add-window-${index}`}
											>
												{t("department.config.staffApp.autoApprove.addWindow")}
											</SystemButton>
										</Box>
									</TableCell>
									<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
										<Typography
											variant="caption"
											sx={{ color: NEUTRAL[600], display: "block", lineHeight: 1.4 }}
											data-testid={`dept-config-auto-approve-logic-${index}`}
										>
											{ruleLogic(row)}
										</Typography>
									</TableCell>
									<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
										<SystemButton
											buttonVariant="borderless"
											size="small"
											leadingIcon={<DeleteOutlined fontSize="small" />}
											onClick={() => removeRow(index)}
											tooltip={t("department.config.staffApp.autoApprove.deleteSet")}
											aria-label={t("department.config.staffApp.autoApprove.deleteSet")}
											data-testid={`dept-config-auto-approve-delete-${index}`}
										/>
									</TableCell>
								</TableRow>
							);
						})}
						{rules.length === 0 && (
							<TableRow>
								<TableCell colSpan={showJobColumn ? 9 : 8} sx={{ borderBottom: "none", p: 0 }}>
									<EmptyState
										icon={<BoltOutlined />}
										title={t("department.config.staffApp.autoApprove.empty.title")}
										hint={t("department.config.staffApp.autoApprove.empty.hint")}
										action={
											<SystemButton
												buttonVariant="secondary"
												size="small"
												leadingIcon={<AddCircleOutlined fontSize="small" />}
												onClick={addRow}
												data-testid="dept-config-auto-approve-empty-add"
											>
												{t("department.config.staffApp.autoApprove.addSet")}
											</SystemButton>
										}
										dataTestId="dept-config-auto-approve-empty"
									/>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};
